import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { DeleteModal } from '@components/shared/TaModal/DeleteModal';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { themeBoxShadow } from '@config/theme';
import { FileUploadIndicator } from '@features/auth/registration/components/FileIndicator';
import { EditableCard } from '@features/dashboard/specialistDashboard/editableCard/editableCard';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import { Button } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultipartUpload, useUploadStore } from 'services/file-manager';
import { useDeleteUserFile, useUpdateUser, useUsersStore } from 'services/users';

const CDN_URL = process.env.REACT_APP_CDN_URL;

export const RecommendationLetters = ({ showButton = true }: { showButton?: boolean }) => {
	const { t } = useTranslation();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [fileToDelete, setFileToDelete] = useState<string | null>(null);
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const removeFile = useDeleteUserFile();
	const { user } = useUsersStore();
	const { uploadFiles, resetStatus, isAllCompleted } = useMultipartUpload();
	const { uploadStatus } = useUploadStore();
	const updateUser = useUpdateUser();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	const handleRemoveFile = (fileId: string) => {
		setFileToDelete(fileId);
		setIsModalOpen(true);
	};

	const confirmRemoveFile = async () => {
		if (fileToDelete) {
			await removeFile(fileToDelete);
			setFileToDelete(null);
			setIsModalOpen(false);
		}
	};

	const cancelRemoveFile = () => {
		setFileToDelete(null);
		setIsModalOpen(false);
	};

	const recommendataionFiles = useMemo(() => {
		return user?.user_files.filter((file) => file.file_section === 'recommendation');
	}, [user]);

	const handleFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(event.target.files || []);
			const allowedTypes = [
				'text/plain', // .txt
				'application/msword', // .doc
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
				'application/pdf', // .pdf
				'application/vnd.ms-excel', // .xls
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
				'application/vnd.ms-powerpoint', // .ppt
				'application/vnd.google-earth.kmz', // .kmz
				'application/vnd.google-earth.kml+xml', // .kml
				'image/jpeg', // .jpg, .jpeg
				'image/png', // .png
				'image/gif', // .gif
				'image/heic', // .heic
				'image/webp', // .webp
				'image/heif', // .heif
			];
			if (files.length > 0) {
				const invalidFiles = files.filter((file) => !allowedTypes.includes(file.type));

				if (invalidFiles.length > 0) {
					showSnackbar(
						'Error',
						'One or more files have invalid type. Please upload valid documents only.',
						'error'
					);
					event.target.value = '';
					return;
				}
				const uploadResults = await uploadFiles(files);

				if (uploadResults) {
					await updateUser({
						file_section: 'recommendation',
						userFiles: uploadResults
							.filter((status) => status.isCompleted)
							.map((status) => ({ file: status.key })),
					});
				}
				event.target.value = '';
				resetStatus();
			}
		},
		[updateUser, uploadFiles, resetStatus, showSnackbar]
	);

	const handleSave = async () => {
		try {
			setIsSubmitting(true);
			// Wait for any pending uploads to complete
			await new Promise((resolve) => setTimeout(resolve, 500));
			setOpen(false);
			setSnackbarOpen(true);
		} catch (error) {
			showSnackbar('Error', 'Failed to save recommendation letters', 'error');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<EditableCard
				className="responsive-card"
				title={t('recommendationLetters')}
				showActions={false}
				onEdit={() => setOpen(true)}
			>
				<div className="flex flex-col gap-[10px]">
					{recommendataionFiles?.map((file) => (
						<a
							key={file.id}
							href={`${CDN_URL}${file.s3_key}`}
							target="_blank"
							rel="noopener noreferrer"
							className="flex gap-[10px] items-center cursor-pointer"
						>
							<AttachFileIcon className="text-primary" />
							<p className="text-primary roboto-bold text-[16px] underline">{file.filename}</p>
						</a>
					))}
				</div>
				<TaModal
					save={handleSave}
					open={open}
					onClose={() => setOpen(false)}
					title={t('recommendationLetters')}
					showActions
					saveButtonLoading={isSubmitting || !isAllCompleted}
					disableSaveButton={!isAllCompleted}
					disableCancelButton={isSubmitting || !isAllCompleted}
				>
					<div>
						<input
							type="file"
							accept=".txt,.doc,.docx,.pdf,.xls,.xlsx,.ppt,.kmz,.kml"
							style={{ display: 'none' }}
							id="pdf-upload"
							onChange={handleFileChange}
						/>
						<label htmlFor="pdf-upload">
							<Button
								variant="contained"
								component="span"
								className="flex items-center justify-center gap-[10px] px-[30px] py-[10px] w-full !my-[20px]"
								disabled={isSubmitting || !isAllCompleted}
							>
								<UploadOutlinedIcon />
								<span className="roboto-bold text-[14px] leading-[140%] uppercase">
									{t('uploadButton')}
								</span>
							</Button>
						</label>
						<div>
							<span className="roboto-regular text-[16px] leading-[140%]">{t('myLetters')}</span>
							<div className="flex flex-col gap-[10px] mt-[10px]">
								{!isAllCompleted &&
									uploadStatus.map((status, index) => (
										<FileUploadIndicator
											completed={status.isCompleted}
											error={Boolean(status.error)}
											key={index}
											label={status.fileName}
											progress={status.progress}
										/>
									))}
								{/* here show the user object userFiles */}
								{recommendataionFiles?.map((file) => (
									<FileUploadIndicator
										completed={true}
										error={false}
										key={file.id}
										label={file.filename}
										removeFile={() => handleRemoveFile(file.id)}
									/>
								))}
							</div>
						</div>
					</div>
				</TaModal>
				<NotificationSnackbar
					open={snackbarOpen}
					onClose={() => setSnackbarOpen(false)}
					title={t('successfullyUpdated')}
					success
					description={t('profileUpdated', {
						title: t('recommendationLetters'),
					})}
				/>
			</EditableCard>
			<DeleteModal
				open={isModalOpen}
				onClose={cancelRemoveFile}
				title={t('deleteTitle')}
				actions={
					<>
						<Button
							sx={{ color: '#343A40', boxShadow: themeBoxShadow.primary }}
							onClick={cancelRemoveFile}
						>
							{t('cancel')}
						</Button>
						<Button
							sx={{ background: '#FD3333', boxShadow: themeBoxShadow.primary }}
							onClick={confirmRemoveFile}
							variant="contained"
						>
							{t('yes')}
						</Button>
					</>
				}
			>
				<p className="text-secondary roboto-regular text-[16px]">{t('deleteText')}</p>
			</DeleteModal>
		</>
	);
};
